import React, { useCallback, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import InteractionService from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import { Poll, PollResult, PollVote } from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type PollProviderProps = {
  interaction: Poll;
  preview: boolean;
  children?: React.ReactNode;
};

export type PollContextProps = {
  votes: PollVote[];
  selection: PollVote[];
  isPreview: boolean;
  onVote: (vote: PollVote) => void;
  onSelect: (vote: PollVote) => void;
  onConfirmSelection: () => void;
  hasVotes: boolean;
  isSlideVoted: (slideId: string) => boolean;
};

const PollContext = React.createContext<PollContextProps>({
  votes: [],
  selection: [],
  isPreview: false,
  hasVotes: false,
  isSlideVoted: () => false,
  onVote: () => undefined,
  onSelect: () => undefined,
  onConfirmSelection: () => undefined,
});

const PollProvider: React.FC<PollProviderProps> = ({
  interaction,
  preview,
  children,
}) => {
  const [votes, setVotes] = useState<PollVote[]>(
    !preview
      ? (LocalStorageService.getVotes(interaction.id) as PollVote[]) || []
      : []
  );
  const [selection, setSelection] = useState<PollVote[]>([]);

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const hasVotes = votes.length > 0;

  const isSlideVoted = useCallback(
    (slideId: string) => votes.some((vote) => vote.slideId === slideId),
    [votes]
  );

  const sendVote = (vote: PollVote) => {
    api.postPollResult(
      vote.interactionId,
      vote.slideId,
      vote.optionId,
      !hasVotes
    );
  };

  const sendVotes = (votesToSend: PollVote[]) => {
    if (votesToSend.length) {
      const options = votesToSend.map((vote) => ({ ...vote, value: 1 }));
      api.postPollResults(votesToSend[0].interactionId, options, !hasVotes);
    }
  };

  const onVote = (vote: PollVote) => {
    const updatedVotes = [...votes, vote];
    if (!preview) {
      sendVote(vote);
      LocalStorageService.setVotes(vote.interactionId, updatedVotes);
      TrackingService.getInstance().trackOnVote();
    }
    setVotes(updatedVotes);
  };

  const onSelect = (vote: PollVote) => {
    setSelection((prev) =>
      prev.some((selected) => selected.optionId === vote.optionId)
        ? prev.filter((selected) => selected.optionId !== vote.optionId)
        : [...prev, vote]
    );
  };

  const onConfirmSelection = () => {
    if (selection.length) {
      const updatedVotes = [...votes, ...selection];
      if (!preview) {
        sendVotes(selection);
        LocalStorageService.setVotes(selection[0].interactionId, updatedVotes);
        TrackingService.getInstance().trackOnVote();
      }
      setSelection([]);
      setVotes(updatedVotes);
    }
  };

  const value = useMemo(
    () => ({
      votes,
      selection,
      isPreview: preview,
      hasVotes,
      isSlideVoted,
      onVote,
      onSelect,
      onConfirmSelection,
    }),
    [votes, selection, preview, hasVotes, isSlideVoted]
  );

  return <PollContext.Provider value={value}>{children}</PollContext.Provider>;
};

const PollConsumer = PollContext.Consumer;

export { PollConsumer, PollProvider };
export default PollContext;

/**
 * Poll Voting Utils
 *
 * @implements getPollVotesCount
 * @implements getPollTotalVotes
 * @implements getPollSympathizersCount
 */
export function getPollVotesCount(results: PollResult[]): number {
  return results.reduce(
    (sum: number, result: PollResult) => sum + result.value,
    0
  );
}

export function getPollTotalVotes(results: PollResult[]): number {
  return results
    .map((result: PollResult) => result.value)
    .reduce((a: number, b: number): number => a + b, 0);
}

export function getPollOptionVotesPercent(
  slideResults: PollResult[],
  optionId: string,
  localStorageParticipated = false,
  localStorageIsUserAnswer = false
): number {
  const optionResults: PollResult[] = slideResults.filter(
    (result: PollResult) => result.optionId === optionId
  );
  const optionResult: PollResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;
  let optionVotes: number = optionResult ? optionResult.value : 0;
  let slideVotes = getPollVotesCount(slideResults);

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    optionVotes += 1;
  }
  if (localStorageParticipated) {
    slideVotes += 1;
  }

  return Math.round((optionVotes / slideVotes) * 100);
}

export function getPollOptionVotes(
  slideResults: PollResult[],
  optionId: string,
  localStorageIsUserAnswer = false
): number {
  const optionResults: PollResult[] = slideResults.filter(
    (result: PollResult) => result.optionId === optionId
  );
  const optionResult: PollResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;

  let optionVotes: number = optionResult ? optionResult.value : 0;

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    optionVotes += 1;
  }

  return optionVotes;
}

export function getPollSympathizersCount(
  results: PollResult[],
  votes: PollVote[]
): number {
  return results
    .filter((result: PollResult) =>
      votes.find((vote: PollVote) => vote.optionId === result.optionId)
    )
    .map((result: PollResult) => result.value)
    .reduce((a: number, b: number): number => a + b, 0);
}
